import {Alert, Button, Card, Divider, Form, Input, Select, Typography, Row, Col, Checkbox} from "antd";
import React from "react";
import {Edit, useDataProvider, useEditContext, useNotify, usePermissions, useQuery, useRedirect} from "react-admin";
import {ExtendedDataProvider} from "@src/types";
import DeleteButton from "@src/components/Common/DeleteButton";
import ModalWithButton from "@src/components/Common/ModalWithButton";
import PackForm from "@src/components/AIProcessors/AIProcessorExtraDataForms/PackForm";
import OptimiserForm from "@src/components/AIProcessors/AIProcessorExtraDataForms/OptimiserForm";
import ValidatorForm from "@src/components/AIProcessors/AIProcessorExtraDataForms/ValidatorForm";

const { Title, Text } = Typography;
const { TextArea } = Input;
const resource = "serviceowner_ai_processors";
const title = "AI Processor"


const AIProcessorCreateForm = ({ onVersionSelect, isReadOnly, selectedVersionData, initialFormData }: { onVersionSelect?: (data: any) => void, isReadOnly?: boolean, selectedVersionData?: any, initialFormData?: any } = {}) => {
    const [form] = Form.useForm();
    const notify = useNotify();
    const redirect = useRedirect();
    const { permissions } = usePermissions();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const { record, loading, loaded } = useEditContext() || {};

    // Use context if available, otherwise use props
    const versionContext = React.useContext(VersionContext);
    const actualIsReadOnly = versionContext?.isReadOnly ?? isReadOnly ?? false;
    const actualSelectedVersionData = versionContext?.selectedVersionData ?? selectedVersionData;
    const actualOnVersionSelect = versionContext?.onVersionSelect ?? onVersionSelect;
    const actualInitialFormData = initialFormData;


    const { data: serviceData } = useQuery({
        type: 'getList',
        resource: 'services',
        payload: { pagination: { page: 1, perPage: 20 }, sort: { field: 'name', order: 'asc' }, filter: {}}
    });
    const { data: llmModelsData } = useQuery({
        type: 'getList',
        resource: 'llm_models',
        payload: { pagination: { page: 1, perPage: 20 }, sort: { field: 'name', order: 'asc' }, filter: {}}
    });

    // Watch fields for conditional rendering
    const actionTypeValue = Form.useWatch('action_type', form);
    const serviceFieldValue = Form.useWatch('service', form);
    const responseSchemaValue = Form.useWatch('response_schema', form);
    const [responseSchemaErrors, setResponseSchemaErrors] = React.useState<string[]>([]);
    const selectedServiceId = typeof serviceFieldValue === 'object' ? serviceFieldValue?.value : serviceFieldValue;
    const [universalExecutorEnabled, setUniversalExecutorEnabled] = React.useState(false);

    React.useEffect(() => {
        if (!selectedServiceId) {
            setUniversalExecutorEnabled(false);
            return;
        }
        let cancelled = false;
        dataProvider.getAction('serviceowner_pack_profile', {
            id: undefined,
            verb: `service/${selectedServiceId}/config`
        })
            .then(({ data }: any) => { if (!cancelled) setUniversalExecutorEnabled(!!data?.universal_executor_enabled); })
            .catch(() => { if (!cancelled) setUniversalExecutorEnabled(false); });
        return () => { cancelled = true; };
    }, [selectedServiceId, dataProvider]);

    React.useEffect(() => {
        // Always reset form first to ensure clean state
        form.resetFields();

        if (actualSelectedVersionData) {
            // Populate form with selected version data (read-only mode)
            form.setFieldsValue({
                name: actualSelectedVersionData.name,
                service: { value: actualSelectedVersionData.service.id, label: actualSelectedVersionData.service.name },
                llm_model: { value: actualSelectedVersionData.llm_model.id, label: actualSelectedVersionData.llm_model.name },
                processor_type: actualSelectedVersionData.processor_type,
                prompt: actualSelectedVersionData.prompt,
                response_schema: actualSelectedVersionData.response_schema,
                extra_data: {
                    ...actualSelectedVersionData.extra_data,
                    include_change_instance: actualSelectedVersionData.extra_data?.include_change_instance || false,
                    enable_pack_context: actualSelectedVersionData.extra_data?.enable_pack_context || false,
                    include_previous_declaration: actualSelectedVersionData.extra_data?.include_previous_declaration || false,
                    include_service_config: actualSelectedVersionData.extra_data?.include_service_config || false,
                    enable_generative_ui: actualSelectedVersionData.extra_data?.enable_generative_ui || false,
                }
            });
        } else if (actualInitialFormData) {
            // Use initial form data (from "Set as current")
            form.setFieldsValue({
                name: actualInitialFormData.name,
                service: { value: actualInitialFormData.service.id, label: actualInitialFormData.service.name },
                llm_model: { value: actualInitialFormData.llm_model.id, label: actualInitialFormData.llm_model.name },
                processor_type: actualInitialFormData.processor_type,
                prompt: actualInitialFormData.prompt,
                response_schema: actualInitialFormData.response_schema,
                extra_data: {
                    ...actualInitialFormData.extra_data,
                    include_change_instance: actualInitialFormData.extra_data?.include_change_instance || false,
                    enable_pack_context: actualInitialFormData.extra_data?.enable_pack_context || false,
                    include_previous_declaration: actualInitialFormData.extra_data?.include_previous_declaration || false,
                    include_service_config: actualInitialFormData.extra_data?.include_service_config || false,
                    enable_generative_ui: actualInitialFormData.extra_data?.enable_generative_ui || false,
                }
            });
        } else if (record) {
            // Reset form to current record data
            form.setFieldsValue({
                name: record.name,
                service: { value: record.service.id, label: record.service.name },
                llm_model: { value: record.llm_model.id, label: record.llm_model.name },
                processor_type: record.processor_type,
                prompt: record.prompt,
                response_schema: record.response_schema,
                extra_data: {
                    ...record.extra_data,
                    include_change_instance: record.extra_data?.include_change_instance || false,
                    enable_pack_context: record.extra_data?.enable_pack_context || false,
                    include_previous_declaration: record.extra_data?.include_previous_declaration || false,
                    include_service_config: record.extra_data?.include_service_config || false,
                    enable_generative_ui: record.extra_data?.enable_generative_ui || false,
                }
            });
        }
    }, [actualSelectedVersionData, actualInitialFormData, record, form]);

    const handleSubmitError = (error: any) => {
        if (error.status == 400) {
            for (const [key, value] of Object.entries<any>(error.body))
                form.setFields([{ name: key, errors: value }])
            if ("response_schema" in error.body) {
                const messages = error.body['response_schema'];
                setResponseSchemaErrors(Array.isArray(messages) ? messages : ["" + messages]);
            }
            if ("non_field_errors" in error.body) {
                notify("" + error.body['non_field_errors'], 'error')
            }
        }
    }

    const onSubmit = (rawValues: any) => {
        if (actualIsReadOnly) {
            notify("Cannot submit while viewing an older version", 'warning');
            return;
        }

        setResponseSchemaErrors([]);

        // Merge hidden fields that antd Form may not collect from non-controlled components
        const values = {
            ...rawValues,
            response_schema: rawValues.response_schema ?? form.getFieldValue('response_schema') ?? null,
        };

        if (typeof values.service == "object")
            values.service = values.service.value

        if (typeof values.llm_model == "object")
            values.llm_model = values.llm_model.value

        // Parse response_schema string to JSON object for the backend JSONField
        if (typeof values.response_schema === 'string' && values.response_schema.trim()) {
            try {
                values.response_schema = JSON.parse(values.response_schema);
            } catch (e) {
                // leave as string if not valid JSON
            }
        } else if (!values.response_schema) {
            values.response_schema = null;
        }
        // Parse generative_ui_schema string to JSON object for the backend
        if (typeof values.extra_data?.generative_ui_schema === 'string' && values.extra_data.generative_ui_schema.trim()) {
            try {
                values.extra_data.generative_ui_schema = JSON.parse(values.extra_data.generative_ui_schema);
            } catch (e) {
                // leave as string if not valid JSON
            }
        } else if (!values.extra_data?.generative_ui_schema) {
            if (values.extra_data) values.extra_data.generative_ui_schema = null;
        }

        if (record) {
            dataProvider.update(resource, { id: record.id, data: values, previousData: { id: record.id } })
                .then(() => {
                    notify(`${title} updated successfully`);
                    redirect(`/${resource}`)
                })
                .catch(handleSubmitError)
        } else {
            dataProvider.create(resource, {data: values})
                .then(() => {
                    notify(`${title} created successfully`);
                    redirect(`/${resource}`)
                })
                .catch(handleSubmitError)
        }
    }


    const formContent = (
        <div>
            {/* Version Dropdown Section */}
            {record && (
                <div style={{ marginBottom: '12px', marginRight: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                    <AIProcessorVersionDropdown />
                </div>
            )}

            {/* Universal Executor Notice - only for config/verify/execution */}
            {universalExecutorEnabled && ['config', 'verify', 'execution'].includes(actionTypeValue) && (
                <Alert
                    message="Universal Executor is enabled for this service"
                    description="The response schema is managed by the Universal Executor and cannot be edited here."
                    type="info"
                    showIcon
                    style={{ margin: '16px', borderRadius: '12px' }}
                />
            )}

            {/* Form Section */}
            <Form
                form={form}
                name="create_ai_validator"
                key={actualInitialFormData ? `form-${JSON.stringify(actualInitialFormData).substring(0, 50)}` : record ? `record-${record.id}` : 'new'}
                labelCol={{ span: 3 }}
                className="row-col"
                style={{margin: "10px"}}
                labelAlign="left"
                initialValues={
                    actualInitialFormData ? {
                        ...actualInitialFormData,
                        service: { value: actualInitialFormData.service.id, label: actualInitialFormData.service.name },
                        llm_model: { value: actualInitialFormData.llm_model.id, label: actualInitialFormData.llm_model.name },
                        extra_data: {
                            ...actualInitialFormData.extra_data,
                            include_change_instance: actualInitialFormData.extra_data?.include_change_instance || false,
                            enable_pack_context: actualInitialFormData.extra_data?.enable_pack_context || false,
                            include_previous_declaration: actualInitialFormData.extra_data?.include_previous_declaration || false,
                            include_service_config: actualInitialFormData.extra_data?.include_service_config || false
                        }
                    } : record ? {
                        ...record,
                        service: { value: record.service.id, label: record.service.name },
                        llm_model: { value: record.llm_model.id, label: record.llm_model.name },
                        extra_data: {
                            ...record.extra_data,
                            include_change_instance: record.extra_data?.include_change_instance || false,
                            enable_pack_context: record.extra_data?.enable_pack_context || false,
                            include_previous_declaration: record.extra_data?.include_previous_declaration || false,
                            include_service_config: record.extra_data?.include_service_config || false
                        }
                    } : {
                        remember: true,
                        extra_data: {
                            include_change_instance: false,
                            enable_pack_context: false,
                            include_previous_declaration: false,
                            include_service_config: false,
                            enable_generative_ui: false,
                            allow_auto_approval: false,
                            allow_auto_rejection: false,
                            send_service_info: false,
                            send_existing_service_items: false
                        }
                    }
                }
                onFinish={onSubmit}
            >
                <Form.Item label={<Text strong>Name</Text>} name="name" rules={[{ required: true, message: 'Please enter a Name' }]}>
                    {actualIsReadOnly ? 
                        <Text style={{ display: 'block', padding: '4px 0', fontSize: '14px' }}>
                            {actualSelectedVersionData?.name || 'N/A'}
                        </Text>
                        : <Input placeholder="Name" disabled={permissions !== "ADMIN"} style={{ width: '50%' }} />
                    }
                </Form.Item>

                <Form.Item label={<Text strong>Service</Text>} name="service" rules={[{ required: true, message: 'Please select a service' }]}>
                    {actualIsReadOnly ?
                        <Text style={{ display: 'block', padding: '4px 0', fontSize: '14px' }}>
                            {actualSelectedVersionData?.service?.name || 'N/A'}
                        </Text>
                        : <Select placeholder="Select Service" style={{ width: '50%' }} disabled={permissions !== "ADMIN"}>
                            {
                                serviceData &&
                                // @ts-ignore
                                serviceData.map((service: any) => <Select.Option key={service.id} value={service.id}>{service.name}</Select.Option>)
                            }
                        </Select>
                    }
                </Form.Item>

                <Form.Item label={<Text strong>LLM Model</Text>} name="llm_model" rules={[{ required: true, message: 'Please select a model' }]}>
                    {actualIsReadOnly ?
                        <Text style={{ display: 'block', padding: '4px 0', fontSize: '14px' }}>
                            {actualSelectedVersionData?.llm_model?.name || 'N/A'}
                        </Text>
                        : <Select placeholder="Select a LLM model" style={{ width: '50%' }} disabled={permissions !== "ADMIN"}>
                            {
                                llmModelsData &&
                                // @ts-ignore
                                llmModelsData.map((llmModel: any) => <Select.Option key={llmModel.id} value={llmModel.id}>{llmModel.name}</Select.Option>)
                            }
                        </Select>
                    }
                </Form.Item>

                <Form.Item label={<Text strong>Type</Text>} name="action_type" rules={[{ required: true, message: 'Please select a type' }]}>
                    {actualIsReadOnly ?
                        <Text style={{ display: 'block', padding: '4px 0', fontSize: '14px' }}>
                            {actualSelectedVersionData?.processor_type || actualSelectedVersionData?.action_type || 'N/A'}
                        </Text>
                        : <Select placeholder="Select Type" style={{ width: '50%' }} disabled={permissions !== "ADMIN"}>
                            <Select.Option value="config">Config</Select.Option>
                            <Select.Option value="verify">Verify</Select.Option>
                            <Select.Option value="execution">Execution</Select.Option>
                            <Select.Option value="optimiser">Optimiser</Select.Option>
                            <Select.Option value="change_instance_validator">Change Instance Validator</Select.Option>
                        </Select>
                    }
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.action_type !== cur.action_type}>
                    {({ getFieldValue }) => getFieldValue('action_type') !== 'optimiser' && (
                        <Form.Item label={<Text strong>Prompt</Text>} name={"prompt"} rules={[{ required: true }]}>
                            {actualIsReadOnly ?
                                <div style={{
                                    padding: '8px 12px',
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px',
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    maxHeight: '200px',
                                    overflow: 'auto'
                                }}>
                                    {actualSelectedVersionData?.prompt || 'No prompt available'}
                                </div>
                                : <TextArea rows={10} placeholder="Prompt" maxLength={10000} />
                            }
                        </Form.Item>
                    )}
                </Form.Item>

                {/* Hidden field persists response_schema in form store regardless of conditional rendering */}
                <Form.Item name="response_schema" hidden><input /></Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.action_type !== cur.action_type}>
                    {({ getFieldValue }) => !['optimiser', 'change_instance_validator'].includes(getFieldValue('action_type')) && !universalExecutorEnabled && (
                        <Form.Item
                            label={<Text strong style={{marginTop: "4px" }}>Response Schema :</Text>}
                            colon={false}
                            validateStatus={responseSchemaErrors.length ? "error" : undefined}
                            help={responseSchemaErrors.length
                                ? <div style={{ whiteSpace: 'pre-wrap' }}>{responseSchemaErrors.join('\n\n')}</div>
                                : undefined}
                        >
                            <ModalWithButton
                                key={`modal-${actualSelectedVersionData?.history_id || actualInitialFormData?.history_id || record?.id || 'new'}`}
                                handleOk={actualIsReadOnly ? () => {} : (value: any) => {
                                    setResponseSchemaErrors([]);
                                    // ModalWithButton returns the editor content as string, or null for empty
                                    if (value != null) {
                                        const serialized = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                                        form.setFieldsValue({ response_schema: serialized });
                                    } else {
                                        form.setFieldsValue({ response_schema: null });
                                    }
                                }}
                                editorType={"json"}
                                buttonTitle={actualIsReadOnly ? "View Schema" : "Edit/View Schema"}
                                initialValue={responseSchemaValue !== undefined
                                    ? (responseSchemaValue ?? "")
                                    : (actualSelectedVersionData?.response_schema || actualInitialFormData?.response_schema || record?.response_schema || "")}
                            />
                        </Form.Item>
                    )}
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.action_type !== cur.action_type}>
                    {({ getFieldValue }) => {
                        const actionType = getFieldValue('action_type');
                        const disabled = actualIsReadOnly || permissions !== "ADMIN";
                        if (['config', 'verify', 'execution'].includes(actionType)) {
                            return (
                                <>
                                    <Divider titlePlacement="start">Options</Divider>
                                    {PackForm(form, disabled)}
                                </>
                            );
                        }
                        if (actionType === 'optimiser') {
                            return (
                                <>
                                    <Divider titlePlacement="start">Options</Divider>
                                    {OptimiserForm(form, disabled)}
                                </>
                            );
                        }
                        if (actionType === 'change_instance_validator') {
                            return (
                                <>
                                    <Divider titlePlacement="start">Options</Divider>
                                    {ValidatorForm(form, disabled, record?.evaluation_result)}
                                </>
                            );
                        }
                        return null;
                    }}
                </Form.Item>

                <Form.Item>
                    {
                        permissions === "ADMIN" && !actualIsReadOnly &&
                        <Button style={{ width: "20%" }} type="primary" htmlType="submit" className="form-button">{record? "Update":"Create"}</Button>
                    }
                    {
                        record &&
                        permissions === "ADMIN" && !actualIsReadOnly &&
                        <DeleteButton title={title} resource={resource} style={{ width: "20%", float: "right" }} record={record} />
                    }
                </Form.Item>
            </Form>
        </div>
    );

    return formContent;
}

// Create a context to share state between components
const VersionContext = React.createContext<{
    selectedVersionData: any;
    isReadOnly: boolean;
    onVersionSelect: (data: any, isLatest?: boolean) => void;
    onSetAsCurrent: () => void;
}>({
    selectedVersionData: null,
    isReadOnly: false,
    onVersionSelect: () => {},
    onSetAsCurrent: () => {}
});

const AIProcessorWrapper = () => {
    const [selectedVersionData, setSelectedVersionData] = React.useState<any>(null);
    const [isReadOnly, setIsReadOnly] = React.useState(false);
    const [initialFormData, setInitialFormData] = React.useState<any>(null);
    const handleVersionSelect = (versionData: any, isLatestVersion = false) => {
        if (versionData && !isLatestVersion) {
            setSelectedVersionData(versionData);
            setIsReadOnly(true);
        } else {
            setSelectedVersionData(null);
            setIsReadOnly(false);
        }
    };

    const handleSetAsCurrent = () => {
        if (selectedVersionData) {
            // Replace initial form data with selected version data
            setInitialFormData({ ...selectedVersionData });
            // Enable the form and clear the selected version
            setSelectedVersionData(null);
            setIsReadOnly(false);
        }
    };

    return (
        <VersionContext.Provider value={{
            selectedVersionData,
            isReadOnly,
            onVersionSelect: handleVersionSelect,
            onSetAsCurrent: handleSetAsCurrent
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Title level={4} style={{ margin: "10px" }}>{title}</Title>
            </div>
            <AIProcessorCreateForm initialFormData={initialFormData} />
        </VersionContext.Provider>
    );
};

const AIProcessorCreate = (props: any) => {
    return (
        <>
            <Card className=" header-solid h-full ant-card pt-0" bordered={false}>
                {props.id ? (
                    <Edit {...props}>
                        <AIProcessorWrapper />
                    </Edit>
                ) : (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <Title level={4} style={{ margin: "10px" }}>{title}</Title>
                        </div>
                        <AIProcessorCreateForm />
                    </>
                )}
            </Card>
        </>
    )
}

const AIProcessorVersionDropdown = () => {
    const [selectedVersion, setSelectedVersion] = React.useState<string | undefined>(undefined);
    const [isLatestSelected, setIsLatestSelected] = React.useState(false);
    const { record } = useEditContext();
    const { onVersionSelect, onSetAsCurrent, selectedVersionData } = React.useContext(VersionContext);

    // Reset dropdown when selectedVersionData becomes null (after "Set as current")
    React.useEffect(() => {
        if (!selectedVersionData) {
            setSelectedVersion(undefined);
            setIsLatestSelected(false);
        }
    }, [selectedVersionData]);

    // Fetch history data for dropdown
    const { data: historyData } = useQuery({
        type: 'getAction',
        resource: resource,
        payload: { id: record?.id, verb: 'history', filter: {}}
    }, { enabled: !!record });

    const getVersionOptions = () => {
        if (!historyData?.results) return [];

        return historyData.results.map((historyItem: any, index: number) => {
            const versionLabel = historyItem.version
                ? `Version ${historyItem.version}`
                : `${new Date(historyItem.history_date).toLocaleDateString()}`;

            return {
                value: historyItem.history_id.toString(),
                label: versionLabel,
                historyItem
            };
        });
    };

    const handleVersionDropdownChange = (value: string) => {
        setSelectedVersion(value);
        if (historyData?.results) {
            const selectedRecord = historyData.results.find((item: any) => item.history_id.toString() === value);
            // Check if this is the latest version (first item in the array, assuming it's sorted by date desc)
            const isLatestVersion = historyData.results[0]?.history_id.toString() === value;
            setIsLatestSelected(isLatestVersion);
            onVersionSelect(selectedRecord, isLatestVersion);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text strong>Show older versions:</Text>
                <Select
                    style={{ width: '180px', height: '32px' }}
                    placeholder="Select version"
                    value={selectedVersion}
                    onChange={handleVersionDropdownChange}
                    allowClear
                    onClear={() => {
                        setSelectedVersion(undefined);
                        setIsLatestSelected(false);
                        onVersionSelect(null);
                    }}
                >
                    {getVersionOptions().map((option: any) => <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>)}
                </Select>
            </div>
            {selectedVersionData && !isLatestSelected && (
                <Button
                    type="primary"
                    onClick={onSetAsCurrent}
                    style={{ height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    Set as current
                </Button>
            )}
        </div>
    );
};

export default AIProcessorCreate;
